// Shared write validators for the vendor item surfaces (HUBX-8474).
//
// Moved from ItemWriteModalComponent so the modal, the simple row and the detailed row
// run ONE copy of each rule rather than three that drift. Pure functions and ValidatorFn
// constants: no @Injectable, no @angular/core, no component state.
//
// @angular/forms IS imported — a ValidatorFn is handed an AbstractControl and there is no
// way to express one without it. That is the only framework surface this module touches,
// and it is types plus the control API, never DI.

import { AbstractControl, FormGroup, UntypedFormArray, ValidationErrors, ValidatorFn } from '@angular/forms';

/** Required string that is present but only whitespace (mirrors the edge Validate). */
export const notBlank: ValidatorFn = (c: AbstractControl): ValidationErrors | null =>
  typeof c.value === 'string' && c.value.length > 0 && c.value.trim().length === 0
    ? { blank: true }
    : null;

/** MOQ <= MXQ when both present and MXQ is a real maximum (>0). -> MOQ_EXCEEDS_MXQ. */
export const moqMxqValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const moq = group.get('moq')?.value;
  const mxq = group.get('mxq')?.value;
  return moq != null && mxq != null && mxq > 0 && moq > mxq ? { moqExceedsMxq: true } : null;
};

/** UnitPrice >= Cost when both present. -> UNIT_PRICE_BELOW_COST. */
export const unitPriceCostValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const cost = group.get('cost')?.value;
  const unitPrice = group.get('unitPrice')?.value;
  return cost != null && unitPrice != null && unitPrice < cost ? { unitPriceBelowCost: true } : null;
};

/** A discount tier is either empty (ignored) or a complete pair with FromQty >= 2
 *  and Price < UnitPrice, and, on an item that names a discount margin (a third-party item,
 *  HUBX-8466), a Price no lower than Cost plus that margin, rounded to cents as the server
 *  rounds it. The margin error carries its figures, for the line that names them.
 *
 *  STRUCTURAL DEPENDENCY, and the reason the form shape is a shared factory rather than a
 *  convention: the tier reads UnitPrice by walking UP — group.parent (the FormArray) then
 *  .parent (the root group). A form built by hand that merely LOOKS the same, with the
 *  tiers somewhere else in the tree, breaks this in SILENCE: the `?.` yields undefined,
 *  the `unitPrice != null` guard is not met, and the tier simply stops being validated.
 *  No error, no ring, a bad discount reaching the server. Build the form with
 *  createItemWriteForm or this rule is decoration. */
export const dqTierValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const fromQty = group.get('fromQty')?.value;
  const price = group.get('price')?.value;
  const hasFrom = fromQty !== null && fromQty !== '' && fromQty !== undefined;
  const hasPrice = dqPriceGiven(price);
  if (!hasFrom && !hasPrice) {
    return null;
  }

  const errors: ValidationErrors = {};
  if (!hasFrom || !hasPrice) {
    errors['incompletePair'] = true;
  }
  if (hasFrom && fromQty < 2) {
    errors['fromQtyBelowMin'] = true;
  }
  const root = group.parent?.parent;
  const unitPrice = root?.get('unitPrice')?.value;
  if (hasPrice && unitPrice != null && price >= unitPrice) {
    errors['priceExceedsUnit'] = true;
  }
  // HUBX-8466: each tier differs from the ones before it, in quantity and in price. Only the
  // later tier is refused, so the ring goes on the repeat and the original stays clean. The
  // values compare as numbers: a masked quantity is held as its digits.
  const siblings = (group.parent as UntypedFormArray | null)?.controls ?? [];
  const before = siblings.slice(0, Math.max(siblings.indexOf(group), 0));
  const present = (v: unknown) => v !== null && v !== '' && v !== undefined;
  const sameQty = before.findIndex(t => hasFrom && present(t.value?.fromQty) && Number(t.value.fromQty) === Number(fromQty));
  if (sameQty >= 0) {
    errors['fromQtyNotUnique'] = { other: sameQty };
  }
  const samePrice = before.findIndex(t => hasPrice && present(t.value?.price) && Number(t.value.price) === Number(price));
  if (samePrice >= 0) {
    errors['priceNotUnique'] = { other: samePrice };
  }
  // HUBX-8466: the more units a tier asks for, the lower its price. The slots can come in any
  // order; sorted by quantity the prices strictly decrease. The tier refused is the one with
  // more units, priced above one with fewer, so the ring goes on its price. An equal price is
  // the uniqueness rule's, not this one's.
  const cheaper = siblings.findIndex(t => t !== group && hasFrom && hasPrice &&
    present(t.value?.fromQty) && present(t.value?.price) &&
    Number(t.value.fromQty) < Number(fromQty) && Number(t.value.price) < Number(price));
  if (cheaper >= 0) {
    errors['priceNotDescending'] = { other: cheaper };
  }
  const margin = root?.get('discountQtyMarginPercent')?.value;
  const cost = root?.get('cost')?.value;
  if (hasPrice && margin != null && cost != null && cost !== '') {
    const floor = Math.round(Number(cost) * (100 + Number(margin))) / 100;
    if (Number(price) < floor) {
      errors['priceBelowCostMargin'] = { fromQty: Number(fromQty), price: Number(price), floor, margin: Number(margin) };
    }
  }
  return Object.keys(errors).length ? errors : null;
};

/** Whether a discount tier's `field` is the input at fault, so the ring goes on it: the empty
 *  half of an incomplete pair, a quantity under 2, a price at or above the sale price, or a
 *  price the server refused (the cost-margin 400, HUBX-8466). The rule only; when to show it is
 *  the surface's (engaged in the modal, edited on a row). */
export function dqFieldError(tier: AbstractControl, field: 'fromQty' | 'price'): boolean {
  if (tier.get(field)?.hasError('server')) {
    return true;
  }
  if (tier.hasError('incompletePair') && !dqHasValue(tier, field)) {
    return true;
  }
  return field === 'fromQty'
    ? tier.hasError('fromQtyBelowMin') || tier.hasError('fromQtyNotUnique')
    : tier.hasError('priceExceedsUnit') || tier.hasError('priceBelowCostMargin') || tier.hasError('priceNotUnique') ||
      tier.hasError('priceNotDescending');
}

/** Control-level error, shown only after the user has engaged the field. */
export function showFieldError(fg: FormGroup, name: string): boolean {
  const c = fg.get(name);
  return !!c && c.invalid && (c.touched || c.dirty);
}

/** Form-group cross-field error, shown once any trigger control is engaged. */
export function showGroupError(fg: FormGroup, key: string, ...triggers: string[]): boolean {
  if (!fg.errors?.[key]) {
    return false;
  }
  return triggers.some(t => {
    const c = fg.get(t);
    return !!c && (c.touched || c.dirty);
  });
}

/** The tier rules that compare a tier with the others (HUBX-8466), by the input they ring. */
const CROSS_TIER_RULES: { [rule: string]: 'fromQty' | 'price' } = {
  fromQtyNotUnique: 'fromQty',
  priceNotUnique: 'price',
  priceNotDescending: 'price'
};

function engaged(c: AbstractControl): boolean {
  return c.touched || c.dirty;
}

/** Per-tier discount error, shown once that tier is engaged. A rule that compares tiers shows
 *  once any tier is (HUBX-8466): typing one tier can break another, and the tier at fault shows
 *  its line though it was not the one typed, as on the listing rows. */
export function dqError(dqControls: UntypedFormArray, index: number, key: string): boolean {
  const g = dqControls.at(index);
  return !!g && g.hasError(key) && (engaged(g) || (key in CROSS_TIER_RULES && engaged(dqControls)));
}

/** The ring on a tier input, with dqError's "when": the input at fault (dqFieldError) once the
 *  tier is engaged, or the input a rule that compares tiers rings once any tier is. */
export function dqInvalid(dqControls: UntypedFormArray, index: number, field: 'fromQty' | 'price'): boolean {
  const g = dqControls.at(index);
  if (!g) {
    return false;
  }
  return engaged(g)
    ? dqFieldError(g, field)
    : engaged(dqControls) && Object.keys(CROSS_TIER_RULES).some(r => CROSS_TIER_RULES[r] === field && g.hasError(r));
}

/** The category pair's ring, on both selects: the leaf refused once the main select is
 *  engaged. The sub list exists only after a main is chosen, so the main is where the pair is
 *  engaged - and with no sub list it is the only select the ring can go on. */
export function showCategoryError(fg: FormGroup): boolean {
  return !!fg.get('categoryId')?.invalid && !!fg.get('categoryMainId')?.touched;
}

/** Whether the write form SHOWS at least one error (HUBX-8466): a ringed field, the ringed
 *  category pair, a shown cross-field rule or a ringed discount tier - the predicates the
 *  modal's template rings with, so a Save blocked by it always has a ring or a line saying
 *  why. When an error is shown (touched / dirty) is the surface's to arrange; this reads it. */
export function hasShownError(fg: FormGroup): boolean {
  if (!fg.invalid) {
    return false;
  }
  const fields = Object.keys(fg.controls).filter(name => name !== 'categoryId' && name !== 'discountQtys');
  if (fields.some(name => showFieldError(fg, name)) || showCategoryError(fg)) {
    return true;
  }
  if (showGroupError(fg, 'moqExceedsMxq', 'moq', 'mxq') || showGroupError(fg, 'unitPriceBelowCost', 'unitPrice', 'cost')) {
    return true;
  }
  const tiers = fg.get('discountQtys') as UntypedFormArray | null;
  return !!tiers?.controls.some(t => (t.touched || t.dirty) && (dqFieldError(t, 'fromQty') || dqFieldError(t, 'price')));
}

/** Whether a DQ sub-field carries a value, driving the '<' / '$' marker that tells the two
 *  stacked unlabeled inputs apart (parity with the inline edit row, where the mask prefix
 *  appears the moment a value is typed). */
export function dqHasValue(group: AbstractControl, key: 'fromQty' | 'price'): boolean {
  const value = group.get(key)?.value;
  return key === 'price' ? dqPriceGiven(value) : value !== null && value !== undefined && value !== '';
}

/** Whether a tier price was given. $0 is no price, as legacy read it ('From Qty' and 'Unit
 *  Price' are required, HUBX-8466): a quantity with $0 is an incomplete pair, ringed on the
 *  price, and $0 alone is an empty tier. The mask may hold the value as a number or a string. */
function dqPriceGiven(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '' && Number(value) !== 0;
}
